import { FunctionComponent, useState, ReactNode } from 'react';
import Button from '../../Input/Button/Button';
import Box from '../../Layout/Box/Box';
import styles from './ConfirmationDialogue.module.scss';
import alert from './svgs/warningYellow.svg';
import paylinkAlert from './svgs/Warning2.svg';
import success from './svgs/successGreen.svg';
import error from './svgs/errorRed.svg';
import fail from './svgs/fail.svg';
import positive from './svgs/positive.svg';
import cautionary from './svgs/cautionary.svg';
import blocked from './svgs/blocked.svg';

export type Status =
  | 'positive'
  | 'cautionary'
  | 'blocked'
  | 'alert'
  | 'paylinkAlert'
  | 'success'
  | 'error'
  | 'fail'
  | 'none';

export const statusMap: { [key in Status]: { src: string; alt: string } } = {
  positive: {
    src: positive,
    alt: 'positive',
  },
  cautionary: {
    src: cautionary,
    alt: 'cautionary',
  },
  blocked: {
    src: blocked,
    alt: 'blocked',
  },
  alert: {
    src: alert,
    alt: 'alert',
  },
  paylinkAlert: {
    src: paylinkAlert,
    alt: 'alert',
  },
  success: {
    src: success,
    alt: 'success',
  },
  error: {
    src: error,
    alt: 'error',
  },
  fail: {
    src: fail,
    alt: 'fail',
  },
  none: {
    src: '',
    alt: '',
  },
};
interface BaseProps {
  variant?: 'paylink' | 'creditCard' | 'onBoarding';
  title: ReactNode;
  subTitle?: ReactNode;
  subInfo?: string;
  content?: string;
  status: Status;
  width?: string;
  buttonRound?: boolean;
  buttonColor?: 'primary' | 'secondary' | 'outlined' | 'none' | 'hover';
  buttonBackgroundColor?: string;
  buttonTextColor?: string;
  buttonCancelColor?: 'primary' | 'secondary' | 'outlined' | 'none' | 'hover';
  buttonCancelTextColor?: string;
  buttonWidth?: string;
  element?: ReactNode;
  'data-testid'?: string;
}

interface Confirm extends BaseProps {
  confirm?: {
    label: string;
    action: () => void;
  };
  cancel?: {
    label: string;
    action: () => void;
  };
  close?: {
    action: () => void;
  };
}

interface Cancel extends BaseProps {
  confirm?: {
    label: string;
    action: () => void;
  };
  cancel: {
    label: string;
    action: () => void;
  };
  close?: {
    action: () => void;
  };
}

export type BaseDialogueProps = Confirm | Cancel;

type FC = FunctionComponent<BaseDialogueProps>;

const BaseDialogue: FC = (props: BaseDialogueProps) => {
  const {
    variant,
    title,
    subTitle,
    subInfo,
    confirm,
    content,
    cancel,
    close,
    status,
    width,
    buttonColor = 'primary',
    buttonBackgroundColor = '',
    buttonTextColor = 'auto',
    buttonCancelColor = 'secondary',
    buttonCancelTextColor = 'auto',
    buttonWidth,
    buttonRound = !!variant,
    element,
    'data-testid': dataTestId,
  } = props;
  const [onClipboard, setOnClipboard] = useState<string | null>(null);

  const handleCopied = (id: 'paylink') => {
    const handlers = {
      paylink: () => {
        void navigator?.clipboard?.writeText(content || '');
      },
    };

    handlers[id]();
    setOnClipboard(id);
  };
  return (
    <div style={{ width }} className={[styles.container, styles[getVariantClass()]].join(' ')} data-testid={dataTestId}>
      {status !== 'none' &&
        (variant === 'paylink' ? (
          <img src={statusMap.paylinkAlert.src} alt={statusMap.paylinkAlert.alt} className={styles.icon} />
        ) : (
          <img src={statusMap[status].src} alt={statusMap[status].alt} className={styles.icon} />
        ))}
      <div className={styles.title} style={status === 'none' ? { marginTop: 0 } : {}}>
        {title}
      </div>
      {subInfo && <div className={styles.subinfo}>{subInfo}</div>}
      {subTitle && <div className={styles.subtitle}>{subTitle}</div>}
      {/* _TODO: why is there a copy button when content has been defined? */}
      {content && (
        <Box flx="row" gap="sm" width="100%" mb="sm" jc="center">
          <div className={styles.contentContainer}>{content}</div>
          <Button
            id="copy-paylink"
            label={onClipboard === 'paylink' ? 'Copied!' : 'Copy'}
            width="auto"
            onClick={() => handleCopied('paylink')}
            color="outlined"
          />
        </Box>
      )}
      {element && element}
      <div className={styles.button_group}>
        {cancel && (
          <Button
            id="confirmationDialogue-cancel"
            color={buttonCancelColor}
            textColor={buttonCancelTextColor}
            label={cancel.label}
            width={buttonWidth || (variant ? '180px' : 'auto')}
            height="38px"
            textSize={variant === 'onBoarding' ? 14 : 1}
            fontUnit={variant === 'onBoarding' ? 'px' : 'em'}
            bold
            round={buttonRound}
            onClick={cancel.action}
          />
        )}
        {variant !== 'onBoarding' && !!variant && confirm && cancel && <span className={styles.divider} />}
        {confirm && (
          <Button
            id="confirmationDialogue-confirm"
            color={variant !== 'onBoarding' ? buttonColor : 'primary'}
            background={buttonBackgroundColor}
            textColor={buttonTextColor}
            label={confirm.label}
            width={buttonWidth || (variant ? '180px' : 'auto')}
            height="38px"
            textSize={variant === 'onBoarding' ? 14 : 1}
            fontUnit={variant === 'onBoarding' ? 'px' : 'em'}
            bold
            round={buttonRound}
            onClick={confirm.action}
          />
        )}
      </div>
      {close && (
        <div className={styles.closeButton}>
          <Button
            id="confirmationDialogue-close"
            iconLocation="center"
            iconSize="xlg"
            color="secondary"
            label=""
            icon="cross"
            height="12px"
            width="12px"
            iconWidth="12px"
            iconHeight="12px"
            textSize={12}
            fontUnit="px"
            noShadow
            noPadding
            onClick={close.action}
          />
        </div>
      )}
    </div>
  );
  function getVariantClass() {
    return variant || '';
  }
};

export default BaseDialogue;
